import React, { useEffect } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate,
} from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Meteor } from 'meteor/meteor';
import { isMobile, isPublicPath, queryStringToObject } from '/imports/utils';
import { useFindOne, useLoggedOutRedirect, useLoggingIn, useSubscribe, useUser } from '/imports/hooks';
import { strings } from '/imports/strings';
import { registerSubscription } from '/imports/subscriptions';
import { sendEmailVerification, setOrgId } from '/imports/api/users';
import { RouteListener } from '/imports/ui/RouteListener';
import { RedirectionPage } from '/imports/ui/RedirectionPage';
import { Publications } from '/imports/publications';
import { AppStyled } from '/imports/ui/AppStyled';
import { LoginPage } from '/imports/ui/LoginPage';
import { ForgotPasswordPage } from '/imports/ui/ForgotPasswordPage';
import { ResetPasswordPage } from '/imports/ui/ResetPasswordPage';
import { CommonRoutes } from '/imports/ui/CommonRoutes';
import { Nav } from '/imports/ui/Nav';
import { CommandPalette } from '/imports/ui/CommandPalette';
import { DashboardPage } from '/imports/ui/DashboardPage';
import { PricingPage } from '/imports/ui/PricingPage';
import { PerformancePage } from '/imports/ui/PerformancePage';
import { FleetPnlPage } from '/imports/ui/FleetPnlPage';
import { TradersPage } from '/imports/ui/TradersPage';
import { TradeInspectorPage } from '/imports/ui/TradeInspectorPage';
import { AnalyticsNotesPage } from '/imports/ui/AnalyticsNotesPage';
import { BotHealthPage } from '/imports/ui/BotHealthPage';
import { AdAttributionPage } from '/imports/ui/AdAttributionPage';
import { MainContent } from '/imports/ui/MainContent';
import { AppHeader } from '/imports/ui/AppHeader';
import { SignupPage } from '/imports/ui/SignupPage';
import { SignupSubmittedPage } from '/imports/ui/SignupSubmittedPage';
import { VerifyEmailPage } from '/imports/ui/VerifyEmailPage';
import { Orgs } from '/imports/api/orgs';
import { Accounts } from '/imports/api/accounts';
import { TermsPage } from '/imports/ui/TermsPage';
import { PrivacyPolicyPage } from '/imports/ui/PrivacyPolicyPage';
import { FooterNav } from '/imports/ui/FooterNav';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { GoogleOauth2RedirectPage } from '/imports/ui/GoogleOauth2RedirectPage';
import { DiscordOauth2RedirectPage } from '/imports/ui/DiscordOauth2Redirect';
import { Oauth2ThankYouPage } from '/imports/ui/Oauth2ThankYouPage';
import { MessagesPage } from '/imports/ui/MessagesPage';
import { EnvironmentPage } from '/imports/ui/EnvironmentPage';
import { DevToolsPage } from '/imports/ui/DevToolsPage';
import { ManagePage } from '/imports/ui/ManagePage';
import { SettingsPage } from '/imports/ui/SettingsPage';
import { ApiDocsPage } from '/imports/ui/ApiDocsPage';
// mogbot admin pages
import { BotsPage } from '/imports/ui/BotsPage';
import { FleetsPage } from '/imports/ui/FleetsPage';
import { FleetPage } from '/imports/ui/FleetPage';
import { BotMessagesPage } from '/imports/ui/BotMessagesPage';
import { CardListsPage } from '/imports/ui/CardListsPage';
import { InventoryPoliciesPage } from '/imports/ui/InventoryPoliciesPage';
import { InventoryPolicyPage } from '/imports/ui/InventoryPolicyPage';
import { CardListPage } from '/imports/ui/CardListPage';
import { BotPage } from '/imports/ui/BotPage';
import { CardsPage } from '/imports/ui/CardsPage';
import { CardPage } from '/imports/ui/CardPage';
import { BuyTargetsPage } from '/imports/ui/BuyTargetsPage';
import { BuyTargetPage } from '/imports/ui/BuyTargetPage';
import { SellTargetsPage } from '/imports/ui/SellTargetsPage';
import { SellTargetPage } from '/imports/ui/SellTargetPage';
import { AssetsPage } from '/imports/ui/AssetsPage';
import { AssetPage } from '/imports/ui/AssetPage';
import { MUsersPage } from '/imports/ui/MUsersPage';
import { MUserPage } from '/imports/ui/MUserPage';
import { EscrowsPage } from '/imports/ui/EscrowsPage';
import { EscrowPage } from '/imports/ui/EscrowPage';
import { TradeItemsPage } from '/imports/ui/TradeItemsPage';
import { TradeItemPage } from '/imports/ui/TradeItemPage';
import { RaritiesPage } from '/imports/ui/RaritiesPage';
import { RarityPage } from '/imports/ui/RarityPage';
import { CardNamesPage } from '/imports/ui/CardNamesPage';
import { CardNamePage } from '/imports/ui/CardNamePage';
import { CardSetsPage } from '/imports/ui/CardSetsPage';
import { CardSetPage } from '/imports/ui/CardSetPage';
import { CardSourcesPage } from '/imports/ui/CardSourcesPage';
import { CardSourcePage } from '/imports/ui/CardSourcePage';
import { PriceSourcesPage } from '/imports/ui/PriceSourcesPage';
import { PriceSourcePage } from '/imports/ui/PriceSourcePage';
import { BlacklistsPage } from '/imports/ui/BlacklistsPage';
import { BlacklistPage } from '/imports/ui/BlacklistPage';
import { BuyCountersPage } from '/imports/ui/BuyCountersPage';
import { BuyCounterPage } from '/imports/ui/BuyCounterPage';
import { LogActivitiesPage } from '/imports/ui/LogActivitiesPage';
import { LogActivityPage } from '/imports/ui/LogActivityPage';
import { LogPacketsPage } from '/imports/ui/LogPacketsPage';
import { LogPacketPage } from '/imports/ui/LogPacketPage';

export const App = () => {
  const pathname = window.location.pathname;
  const publicPath = isPublicPath();
  const loggingIn = useLoggingIn();
  const user = useUser();
  const userId = user?._id;
  const orgId = user?.orgId;
  const isLoadingOrgs = useSubscribe(Publications.listMyOrgs);
  registerSubscription(Publications.listMyOrgs, isLoadingOrgs);
  const loadingOrgs = isLoadingOrgs();
  const account = useFindOne(() => Accounts.find({ userId, orgId }), [`${userId}_${orgId}`]);
  useLoggedOutRedirect();
  const dark = (user?.settings?.theme || 'light') === 'dark';
  verificationToastEffect(user, loadingOrgs, pathname);
  const isUnreadMessagesLoading = useSubscribe(Publications.unreadMessageReceipts);
  registerSubscription(Publications.unreadMessageReceipts, isUnreadMessagesLoading);
  const loading = loggingIn || loadingOrgs;
  if (loading && !publicPath) return <LoadingFullScreen />;
  const query = queryStringToObject();
  if (shouldReloadForOrg(user, query.redirectOrgId)) {
    setOrgId.call(query.redirectOrgId);
    setTimeout(() => window.location.reload());
    return <LoadingFullScreen />;
  }
  if (publicPath) {
    return (
      <AppStyled dark={dark}>
        <Router>
          <RouteListener />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup/:code" element={<SignupPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup-submitted" element={<SignupSubmittedPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/oauth2-complete" element={<Oauth2ThankYouPage />} />
            <Route path="/google-oauth2-redirect" element={<GoogleOauth2RedirectPage />} />
            <Route path="/discord-oauth2-redirect" element={<DiscordOauth2RedirectPage />} />
            <Route path="/r" element={<RedirectionPage />} />
            <Route path="/" element={<Navigate to={Meteor.user()?.settings?.clientPath || '/'} replace />} />
          </Routes>
        </Router>
        <ToastContainer position="top-right" />
      </AppStyled>
    );
  }
  return (
    <AppStyled dark={dark}>
      <Router>
        <RouteListener />
        {/* Inside Router: the palette navigates, so it needs the router context. */}
        <CommandPalette account={account} />
        <Nav account={account} items={[]} />
        <MainContent>
          <AppHeader />
          <Routes>
            {CommonRoutes('')}
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/environment" element={<EnvironmentPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/fleet-pnl" element={<FleetPnlPage />} />
            <Route path="/traders" element={<TradersPage />} />
            <Route path="/trade-inspector" element={<TradeInspectorPage />} />
            <Route path="/analytics-notes" element={<AnalyticsNotesPage />} />
            <Route path="/bot-health" element={<BotHealthPage />} />
            <Route path="/ad-attribution" element={<AdAttributionPage />} />
            <Route path="/devtools" element={<DevToolsPage />} />
            <Route path="/manage" element={<ManagePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />

            {/* mogbot admin — all guarded to super users via method-level superUserGuard */}
            <Route path="/bots/:_id" element={<BotPage />} />
            <Route path="/bots" element={<BotsPage />} />
            <Route path="/fleets/:_id" element={<FleetPage />} />
            <Route path="/fleets" element={<FleetsPage />} />
            <Route path="/bot-messages" element={<BotMessagesPage />} />
            <Route path="/card-lists/:_id" element={<CardListPage />} />
            <Route path="/card-lists" element={<CardListsPage />} />
            <Route path="/inventory-policies/:_id" element={<InventoryPolicyPage />} />
            <Route path="/inventory-policies" element={<InventoryPoliciesPage />} />
            <Route path="/cards/:_id" element={<CardPage />} />
            <Route path="/cards" element={<CardsPage />} />
            <Route path="/buy-targets/:_id" element={<BuyTargetPage />} />
            <Route path="/buy-targets" element={<BuyTargetsPage />} />
            <Route path="/sell-targets/:_id" element={<SellTargetPage />} />
            <Route path="/sell-targets" element={<SellTargetsPage />} />
            <Route path="/assets/:_id" element={<AssetPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/musers/:_id" element={<MUserPage />} />
            <Route path="/musers" element={<MUsersPage />} />
            <Route path="/escrows/:_id" element={<EscrowPage />} />
            <Route path="/escrows" element={<EscrowsPage />} />
            <Route path="/trade-items/:_id" element={<TradeItemPage />} />
            <Route path="/trade-items" element={<TradeItemsPage />} />
            <Route path="/rarities/:_id" element={<RarityPage />} />
            <Route path="/rarities" element={<RaritiesPage />} />
            <Route path="/card-names/:_id" element={<CardNamePage />} />
            <Route path="/card-names" element={<CardNamesPage />} />
            <Route path="/sets/:_id" element={<CardSetPage />} />
            <Route path="/sets" element={<CardSetsPage />} />
            <Route path="/card-sources/:_id" element={<CardSourcePage />} />
            <Route path="/card-sources" element={<CardSourcesPage />} />
            <Route path="/price-sources/:_id" element={<PriceSourcePage />} />
            <Route path="/price-sources" element={<PriceSourcesPage />} />
            <Route path="/blacklists/:_id" element={<BlacklistPage />} />
            <Route path="/blacklists" element={<BlacklistsPage />} />
            <Route path="/buy-counters/:_id" element={<BuyCounterPage />} />
            <Route path="/buy-counters" element={<BuyCountersPage />} />
            <Route path="/log-activities/:_id" element={<LogActivityPage />} />
            <Route path="/log-activities" element={<LogActivitiesPage />} />
            <Route path="/log-packets/:_id" element={<LogPacketPage />} />
            <Route path="/log-packets" element={<LogPacketsPage />} />

            <Route path="/r" element={<RedirectionPage />} />
            {/*
              HomePage was a placeholder whose only content was a link back to
              itself. Land on something useful instead. The dashboards are
              super-user only (they guard with NotPermittedPage), so anyone else
              goes to the first page their nav actually offers rather than to a
              refusal.
            */}
            <Route path="/" element={<Navigate to={user?.isSuperUser ? '/dashboard' : '/messages'} replace />} />
          </Routes>
          {isMobile() && <FooterNav />}
        </MainContent>
      </Router>
      <ToastContainer position="top-center" />
    </AppStyled>
  );
};

const verificationToastEffect = (user: Meteor.User | undefined, loading: boolean, pathname: string) => {
  return;
  const resend = async () => {
    try {
      await sendEmailVerification.call();
      toast.success(strings.Sent());
    }
    catch (error) {
      console.log(error);
      toast.error(`${error}`);
    }
  };
  useEffect(() => {
    setTimeout(async () => {
      if (user?.isSuperUser) return;
      if (!user || loading || pathname?.endsWith('/verify-email')) return;
      if (user && !user?.verified && !loading) toast.info(<span onClick={() => resend()}>{strings.VerificationRequiredMessage()}</span>, { autoClose: false });
    }, 3000);
  }, [`${user?._id}_${loading}`]);
};

const shouldReloadForOrg = (user?: Meteor.User, orgId?: string) => {
  return orgId && user?.orgId !== orgId && (user?.isSuperUser || Orgs.find().map((o) => o._id).includes(orgId));
};
